// Tile dressing (dominion-path visuals, M4): one small path prop on owned
// tiles — ore for Industry, crystals for Science, candles for Faith, runes
// for Arcane, mushrooms for Nature, bones for Chaos — so the map texture
// tells the story quietly (spec §2.1 dressing column). Pure layout here-ish:
// the placement function is exported for tests; rendering is two instanced
// meshes per archetype (body + accent), the same batch pattern as everywhere
// else. Dark-aligned owners get dark-toned palettes (spec §2.2).

import type { SceneNode } from '@aod/protocol';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { bakeShade, merge } from './TerrainScatterMaterials.js';
import { hexNoise } from './elevation.js';

type Archetype = 'industry' | 'science' | 'faith' | 'arcane' | 'nature' | 'chaos';

// --- Geometry (authored small: dressing never competes with scatter trees).
// Parts are baked (vertex shading) so `merge` can join them; the white-ish
// bake lets the per-instance color carry the palette. ---

let dressingSeed = 401;
function baked(g: THREE.BufferGeometry, bottom = 0.7, top = 1): THREE.BufferGeometry {
  dressingSeed += 1;
  return bakeShade(g, { bottom, top, jitter: 0.08, seed: dressingSeed });
}

function geoFor(archetype: Archetype): THREE.BufferGeometry {
  switch (archetype) {
    case 'industry':
      // Ore tailings: a couple of dark rocks.
      return merge([
        baked(new THREE.DodecahedronGeometry(0.045, 0).translate(0, 0.03, 0), 0.62, 0.95),
        baked(new THREE.DodecahedronGeometry(0.03, 0).translate(0.05, 0.02, 0.02), 0.62, 0.95),
      ]);
    case 'science':
      // A crystal shard cluster.
      return merge([
        baked(
          new THREE.OctahedronGeometry(0.05, 0).scale(0.6, 1.6, 0.6).translate(0, 0.06, 0),
          0.85,
          1.08,
        ),
        baked(
          new THREE.OctahedronGeometry(0.032, 0).scale(0.6, 1.4, 0.6).translate(0.05, 0.04, -0.02),
          0.85,
          1.08,
        ),
      ]);
    case 'faith':
      // Candle cluster: three small pale cylinders.
      return merge([
        baked(new THREE.CylinderGeometry(0.014, 0.016, 0.05, 6).translate(0, 0.025, 0), 0.9, 1.05),
        baked(
          new THREE.CylinderGeometry(0.012, 0.014, 0.038, 6).translate(0.035, 0.019, 0.02),
          0.9,
          1.05,
        ),
        baked(
          new THREE.CylinderGeometry(0.012, 0.014, 0.032, 6).translate(-0.03, 0.016, 0.025),
          0.9,
          1.05,
        ),
      ]);
    case 'arcane':
      // A rune-scribed pebble slab.
      return baked(new THREE.BoxGeometry(0.07, 0.05, 0.03).translate(0, 0.025, 0), 0.8, 1.1);
    case 'nature':
      // Mushroom: stem + cap.
      return merge([
        baked(
          new THREE.CylinderGeometry(0.012, 0.016, 0.045, 5).translate(0, 0.022, 0),
          0.85,
          1.02,
        ),
        baked(
          new THREE.SphereGeometry(0.034, 6, 5, 0, Math.PI * 2, 0, Math.PI / 2).translate(
            0,
            0.045,
            0,
          ),
          0.8,
          1.05,
        ),
      ]);
    case 'chaos':
      // Bone pile: crossed bones (cylinder + end knobs), tilted at placement.
      return merge([
        baked(
          new THREE.CylinderGeometry(0.009, 0.009, 0.075, 5)
            .rotateZ(Math.PI / 2)
            .translate(0, 0.014, 0),
          0.9,
          1.05,
        ),
        baked(new THREE.SphereGeometry(0.014, 5, 4).translate(0.038, 0.014, 0), 0.9, 1.05),
        baked(new THREE.SphereGeometry(0.014, 5, 4).translate(-0.038, 0.014, 0), 0.9, 1.05),
        baked(
          new THREE.CylinderGeometry(0.008, 0.008, 0.06, 5)
            .rotateZ(Math.PI / 2.6)
            .translate(0.01, 0.03, 0.02),
          0.9,
          1.05,
        ),
      ]);
  }
}

const GEOS: Record<Archetype, THREE.BufferGeometry> = {
  industry: geoFor('industry'),
  science: geoFor('science'),
  faith: geoFor('faith'),
  arcane: geoFor('arcane'),
  nature: geoFor('nature'),
  chaos: geoFor('chaos'),
};

// Per-archetype body palettes (sRGB hex). Arcane runs brighter than
// everything else on purpose — its "glow" read.
const PALETTES: Record<Archetype, readonly string[]> = {
  industry: ['#4a4440', '#554e48'],
  science: ['#9fc6cc', '#b1d4d9'],
  faith: ['#e8e0cc', '#efe8d6'],
  arcane: ['#8f6fd0', '#a184dd'],
  nature: ['#d8cfc0', '#c4bbae'],
  chaos: ['#ded6c4', '#cfc7b6'],
};

function darkTone(hex: string): string {
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;
  const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  return `#${[r * 0.42 + 0.02, g * 0.42 + 0.02, b * 0.46 + 0.035]
    .map(to255)
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
}

function pick<T>(list: readonly T[], h: number): T {
  return list[Math.min(list.length - 1, Math.floor(h * list.length))]!;
}

// --- Layout (pure, exported for tests) ---

export interface TileDressingItem {
  readonly tileKey: string;
  readonly archetype: Archetype;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotY: number;
  readonly rotZ: number;
  /** Linear-space RGB. */
  readonly bodyColor: readonly [number, number, number];
}

const _c = new THREE.Color();

function weathered(hex: string, h: number): [number, number, number] {
  _c.set(hex);
  _c.offsetHSL(0, 0, (h - 0.5) * 0.12);
  return _c.toArray() as [number, number, number];
}

/**
 * Dressing items for the visibility-filtered tiles. Roughly 55% of owned
 * tiles carry one (deterministic per tile), placed off-centre so nothing
 * fights the tile's own scatter. Pure: same tiles in, same items out.
 */
export function tileDressing(
  tiles: ReadonlyArray<SceneNode>,
  visibleCoords: ReadonlySet<string> | null | undefined,
): TileDressingItem[] {
  const out: TileDressingItem[] = [];
  for (const tile of tiles) {
    const props = (tile.props ?? {}) as Record<string, unknown>;
    const q = props.q as number | undefined;
    const r = props.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    if (visibleCoords && !visibleCoords.has(`${q},${r}`)) continue;
    // `controlledBy` is null for unclaimed land; 0 is the human, a real owner.
    const owner = props.controlledBy as number | null | undefined;
    if (owner === null || owner === undefined) continue;
    const archetype = props.controllerArchetype as Archetype | undefined;
    if (!archetype || !(archetype in PALETTES)) continue;
    const alignment = props.controllerAlignment as string | undefined;

    // ~55% of tiles, deterministic.
    const presence = hexNoise(q * 1.31 + 0.7, r * 1.73 - 0.4);
    if (presence > 0.55) continue;

    const palette = PALETTES[archetype];
    const dark = alignment === 'dark';
    const bodyHex = dark ? darkTone(pick(palette, presence)) : pick(palette, presence);

    const angle = hexNoise(q * 2.9 + 5, r * 3.1 - 2) * Math.PI * 2;
    const radius = 0.5 + hexNoise(q + 9, r - 6) * 0.15; // toward the edge
    const [cx, , cz] = tile.transform.position;
    const h = hexNoise(q * 5.7, r * 4.3);
    out.push({
      tileKey: `${q},${r}`,
      archetype,
      x: cx + Math.cos(angle) * radius,
      y: tile.transform.position[1],
      z: cz + Math.sin(angle) * radius,
      rotY: h * Math.PI * 2,
      // Chaos dressing tilts; arcane slabs lean a little (spec §2.3).
      rotZ: archetype === 'chaos' ? 0.3 + h * 0.3 : archetype === 'arcane' ? 0.1 + h * 0.08 : 0,
      bodyColor: weathered(bodyHex, h),
    });
  }
  return out;
}

// --- Rendering ---

const dressingMat = new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)', vertexColors: true });

function ArchetypeDressingBatch({
  archetype,
  items,
}: {
  readonly archetype: Archetype;
  readonly items: TileDressingItem[];
}) {
  const ref = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const mesh = ref.current;
    if (!mesh) return;
    const obj = new THREE.Object3D();
    const col = new THREE.Color();
    items.forEach((it, i) => {
      obj.position.set(it.x, it.y, it.z);
      obj.rotation.set(it.rotZ, it.rotY, 0);
      obj.scale.setScalar(1);
      obj.updateMatrix();
      mesh.setMatrixAt(i, obj.matrix);
      const rgb = it.bodyColor;
      mesh.setColorAt(i, col.setRGB(rgb[0], rgb[1], rgb[2]));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.computeBoundingSphere();
  }, [items]);

  return (
    <instancedMesh
      args={[GEOS[archetype], dressingMat, items.length]}
      ref={ref}
      frustumCulled={false}
    />
  );
}

export function TileDressing({
  tiles,
  visibleCoords,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly visibleCoords?: ReadonlySet<string> | null;
}) {
  const items = useMemo(() => tileDressing(tiles, visibleCoords), [tiles, visibleCoords]);
  const byArchetype = useMemo(() => {
    const groups = new Map<Archetype, TileDressingItem[]>();
    for (const it of items) {
      const list = groups.get(it.archetype);
      if (list) list.push(it);
      else groups.set(it.archetype, [it]);
    }
    return groups;
  }, [items]);

  return (
    <>
      {[...byArchetype.entries()].map(([archetype, list]) => (
        <ArchetypeDressingBatch key={archetype} archetype={archetype} items={list} />
      ))}
    </>
  );
}
